import {
    UserExaminationAttemptDTO,
    PaymentProofDTO,
    RetakeEligibilityResponse,
    InitiateAppealGrantedRetakeRequest,
    StandardApiResponse,
    RetakeInitiationResponse,
    UnifiedUserExamStatusResponse,
    EducationProgramExamStatus
} from "@/types/examination.types";
import axios from "@/axios.config";
import { AxiosError } from "axios";
import { ApiResponse } from "@/types/fetch.type";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Initiate exam retake with optional payment method and user ID
export const useInitiateRetake = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<RetakeInitiationResponse>, AxiosError<{ message: string }>, FormData | { educationProgramId: string; councilId?: string; userId?: string; paymentMethod?: string }>({
        mutationFn: async (data) => {
            let formData: FormData;
            
            // If data is already FormData (for manual payment with files), use it directly
            if (data instanceof FormData) {
                formData = data;
            } else {
                // Otherwise, create FormData from the object
                formData = new FormData();
                formData.append('educationProgramId', data.educationProgramId);
                if (data.councilId) {
                    formData.append('councilId', data.councilId);
                }
                if (data.userId) {
                    formData.append('userId', data.userId);
                }
                if (data.paymentMethod) {
                    formData.append('paymentMethod', data.paymentMethod);
                }
            }
            
            const response = await axios.post<ApiResponse<RetakeInitiationResponse>>(`/exam-retakes/initiate`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        },
        onSuccess: (response) => {
            const attemptStatus = response.data.attempt.status;
            const hasPayment = response.data.payment !== null;
            
            // Only invalidate queries if it's not an Irembo payment (payment will be handled in component)
            if (!hasPayment || attemptStatus !== 'PAYMENT_REQUIRED') {
                queryClient.invalidateQueries({ queryKey: ['exam-retakes'] });
                queryClient.invalidateQueries({ queryKey: ['exam-sessions'] });
                queryClient.invalidateQueries({ queryKey: ['exam-retakes', 'eligibility'] });
            }
        }
    });
};

// Initiate appeal-granted retake (New)
export const useInitiateAppealGrantedRetake = () => {
    const queryClient = useQueryClient();

    return useMutation<StandardApiResponse<UserExaminationAttemptDTO>, AxiosError<{ message: string }>, InitiateAppealGrantedRetakeRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<StandardApiResponse<UserExaminationAttemptDTO>>(
                `/exam-retakes/appeal-granted/${data.userId}?councilId=${data.councilId}&educationProgramId=${data.educationProgramId}&appealReference=${data.appealReference}`
            );
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Appeal-granted retake initiated successfully. No payment required.',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ['exam-retakes'] });
            queryClient.invalidateQueries({ queryKey: ['exam-sessions'] });
            queryClient.invalidateQueries({ queryKey: ['exam-retakes', 'eligibility'] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to initiate appeal-granted retake',
                color: 'red'
            });
        }
    });
};

// Get exam attempts by user ID - Updated to use educationProgramId
export const useGetUserExamAttempts = (userId?: string, educationProgramId?: string) => {
    return useQuery<ApiResponse<UserExaminationAttemptDTO[]>, AxiosError>({
        queryKey: ["exam-retakes", "user", userId, "attempts", educationProgramId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<UserExaminationAttemptDTO[]>>(`/exam-retakes/user/${userId}/attempts?educationProgramId=${educationProgramId}`);
            return response.data;
        },
        enabled: !!userId && !!educationProgramId,
    });
};

// 🆕 New Education Program specific endpoints
export const useGetUserExamAttemptsByProgram = (userId?: string, educationProgramId?: string, councilId?: string) => {
    return useQuery<ApiResponse<UserExaminationAttemptDTO[]>, AxiosError>({
        queryKey: ["exam-retakes", "user", userId, "program", educationProgramId, "attempts", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<UserExaminationAttemptDTO[]>>(`/exam-retakes/user/${userId}/program/${educationProgramId}/attempts?councilId=${councilId}`);
            return response.data;
        },
        enabled: !!userId && !!educationProgramId && !!councilId,
    });
};

// 🆕 New Education Program specific eligibility check
export const useCheckRetakeEligibilityByProgram = (userId?: string, educationProgramId?: string, councilId?: string) => {
    return useQuery<ApiResponse<RetakeEligibilityResponse>, AxiosError>({
        queryKey: ["exam-retakes", "eligibility", "program", userId, educationProgramId, councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<RetakeEligibilityResponse>>(`/exam-retakes/user/${userId}/program/${educationProgramId}/eligibility?councilId=${councilId}`);
            return response.data;
        },
        enabled: !!userId && !!educationProgramId && !!councilId,
    });
};

// Submit payment proof
export const useSubmitPaymentProof = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<PaymentProofDTO>, AxiosError<{ message: string }>, { amount: number; paymentReference: string; proofDocument?: File }>({
        mutationFn: async (data) => {
            const formData = new FormData();
            formData.append('amount', data.amount.toString());
            formData.append('paymentReference', data.paymentReference);
            if (data.proofDocument) {
                formData.append('proofDocument', data.proofDocument);
            }

            const response = await axios.post<ApiResponse<PaymentProofDTO>>(`/exam-retakes/payment-proof`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Payment proof submitted successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ['exam-retakes'] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to submit payment proof',
                color: 'red'
            });
        }
    });
};

// Get pending payment proofs
export const useGetPendingPaymentProofs = () => {
    return useQuery<ApiResponse<PaymentProofDTO[]>, AxiosError>({
        queryKey: ["exam-retakes", "payment-proofs", "pending"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<PaymentProofDTO[]>>(`/exam-retakes/payment-proofs/pending`);
            return response.data;
        },
    });
};

// Approve/reject payment proof
export const useApprovePaymentProof = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<PaymentProofDTO>, AxiosError<{ message: string }>, { paymentProofId: string; approved: boolean; notes?: string }>({
        mutationFn: async ({ paymentProofId, approved, notes }) => {
            const response = await axios.put<ApiResponse<PaymentProofDTO>>(`/exam-retakes/payment-proofs/${paymentProofId}/approve`, {
                approved,
                notes
            });
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Payment proof reviewed successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ['exam-retakes'] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to review payment proof',
                color: 'red'
            });
        }
    });
};

// Get attempt information for a user and education program - Updated to use educationProgramId
export const useGetAttemptInfo = (userId?: string, educationProgramId?: string) => {
    return useQuery<ApiResponse<{ currentAttemptCount: number; remainingAttempts: number; maxAttempts: number; canRetake: boolean; requiresPayment: boolean }>, AxiosError>({
        queryKey: ["exam-retakes", "attempt-info", userId, educationProgramId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<{ currentAttemptCount: number; remainingAttempts: number; maxAttempts: number; canRetake: boolean; requiresPayment: boolean }>>(`/exam-sessions/attempt-info/${userId}/${educationProgramId}`);
            return response.data;
        },
        enabled: !!userId && !!educationProgramId,
    });
};


// 🆕 Unified endpoint for comprehensive user exam status
export const useGetUserExamStatus = (userId?: string, educationProgramId?: string) => {
    return useQuery<ApiResponse<UnifiedUserExamStatusResponse>, AxiosError>({
        queryKey: ["exam-retakes", "exam-status", userId, educationProgramId],
        queryFn: async () => {
            const url = educationProgramId 
                ? `/exam-retakes/user/${userId}/status?educationProgramId=${educationProgramId}`
                : `/exam-retakes/user/${userId}/status`;
            const response = await axios.get<ApiResponse<UnifiedUserExamStatusResponse>>(url);
            return response.data;
        },
        enabled: !!userId,
    });
}; 